import { Helpers } from '../core/src/helpers';

// TODO: further optimization we can specify image width for components that need smaller images
const getImageLink = (url, resize) => `${url}${resize ? '?imwidth=270&imdensity=1' : ''}`;

const mapBundleProducts = (bundleProducts, priceObj) => {
    if (bundleProducts && bundleProducts.length > 0) {
        return bundleProducts.map(p => {
            const { name: bundleProductTitle, imageUrl, catentryId } = p;
            const price = (priceObj.items || {})[catentryId];
            const { salePrice } = price || {};
            return {
                bundlePricePrefix: 'Worth',
                bundleProductImage: {
                    src: imageUrl,
                    alt: bundleProductTitle,
                },
                bundleProductTitle,
                bundleProductPrice: salePrice,
            };
        });
    }
    return undefined;
};

const handleCompare = (product, toggleCompareProduct) => () => {
    toggleCompareProduct(product, true);
};

const handleAddToCart = (product, addToCartHook, price, callback, isAddToQuote) => () => {
    const { product_type, productUrl } = product;
    const { isOOS } = price;
    const { addToCart, goToCTOPage, launchCartFlyout, goToGiftCardPage } = addToCartHook;
    if (product_type === 'CTO') {
        goToCTOPage(product, 1);
    } else if (['VGC', 'PGC'].includes(product_type)) {
        goToGiftCardPage(Helpers.getRelativePath(productUrl));
    } else if (!isOOS || isAddToQuote) {
        addToCart(product, 1).then(() => {
            launchCartFlyout && launchCartFlyout();
        });
    }
    callback && callback('add', product);
};

const handleUpdateCart = (product, quantity, addToCartHook, callback) => () => {
    const { updateCartBySKU } = addToCartHook;
    updateCartBySKU && updateCartBySKU(product.sku, quantity);
    callback && callback(quantity === 0 ? 'remove' : 'update', product);
};

export const getCTAText = (product, priceObj) => {
    const { ctaAddToCart, product_type } = product || {};
    const { ctaText, customizeText, isccf, isOOS } = priceObj || {};

    const isAddToQuote = isccf && isOOS && product_type === 'STO';
    if (isAddToQuote) return 'ADD TO QUOTE';

    const liveCtaText = (priceObj || !(priceObj || {}).priceFetchFailed) && (customizeText ? customizeText : ctaText);
    return liveCtaText || ctaAddToCart;
};

const getButtonAction = (product, priceObj) => {
    const { product_type, prdClass } = product || {};
    const { customizeText } = priceObj || {};
    const productType = product_type || prdClass;
    return ['CTO', 'VGC', 'PGC'].includes(productType) || customizeText ? 'go' : 'add';
};

/**
 * Derives commonly use props for attachable product and price information
 * @param {*} product
 * @param {*} price
 * @param {*} hooks - Pass in hook, like useAddToCart and useProductCompare
 */
export const getAttachableProductProps = (product, priceObj, onAttach, hooks, options) => {
    const { product_type, prdClass } = product;
    // NOTE: ignore product ctaViewDetails always use "Learn More" ticket HPEDT-9825
    const ctaViewDetails = 'Learn More';
    const { resizeImage = false, translations, detachable, attached, showOfferMessage } = options || {};
    const images = product.images.map(img => ({
        ...img,
        url: getImageLink(img.image || img.src || img.lrg, resizeImage),
    }));
    const { addToCart } = hooks || {};
    const productType = product_type || prdClass;
    const {
        regularPrice,
        salePrice,
        tierMsg,
        locale,
        currency,
        isccf,
        gmPoints,
        stock = 0,
        ctoLink: customizeUrl,
        contingentOfferMessage,
    } = priceObj || {};
    const price = salePrice
        ? {
              regularPrice,
              salePrice,
              productType,
              locale,
              currency,
              tierMsg,
              priceDifference: Math.max(regularPrice - salePrice, 0),
          }
        : {};
    const link = {
        url: product.ctaViewDetailsLink,
        text: ctaViewDetails,
    };
    const [image = {}] = images;
    const { buttons = {}, ...restTranslations } = translations || {};
    const productTranslations = {
        buttons: {
            attach: getCTAText(product, priceObj),
            detach: 'IN CART',
            showMore: 'Show More',
            showLess: 'Show Less',
            ...buttons,
        },
        ...restTranslations,
    };
    const attachHandler = !addToCart
        ? undefined
        : detachable && attached
        ? handleUpdateCart(product, 0, addToCart, onAttach)
        : handleAddToCart(
              { ...product, listPrice: priceObj && (priceObj.salePrice || priceObj.regularPrice) },
              addToCart,
              priceObj,
              onAttach
          );
    const productUrl = product.productUrl || link.url;

    const productTileProps = {
        ...product,
        image,
        link,
        /**The RecommendedAccessories component doesnt quite match the AttachableProductTile props */
        productUrl,
        customizeUrl,
        url: productUrl,
        offerMessage: contingentOfferMessage || product.violatorMessage || product.violatorMsg,
        showOfferMessage,
        /*temp no such data*/
        description: product.prdOverview,
        translations: productTranslations,
        attachTimeout: 1000,
        //TODO: need to create a stellar jira ticket to add support for this on Attachable
        purchaseBtnIcon: getButtonAction(product, priceObj),
        onAttach: attachHandler,
        price,
        isAgent: isccf,
        grossMargin: gmPoints,
        availableInStock: stock.toString(),
        attached,
    };
    return productTileProps;
};

/**
 * Derives commonly use props from product and price information
 * @param {*} product
 * @param {*} price
 * @param {*} hooks - Pass in hook, like useAddToCart and useProductCompare
 */
function getProductProps(product, priceObj, hooks, options = { resizeImage: false }) {
    const {
        ctaViewDetails = 'View Details',
        ctaViewDetailsLink,
        product_type,
        prdClass,
        sku,
        rewardsBadge,
        productUrl,
    } = product;
    const { addToCart, compare = {} } = hooks || {};
    const productType = product_type || prdClass;
    const { isSelected, toggleCompareProduct } = compare;

    const {
        regularPrice,
        salePrice,
        tierMsg,
        stockMessage,
        stockStatus,
        isOOS,
        locale,
        currency,
        gmPoints,
        stock = 0,
        tierPrice,
        isccf,
        ctoLink,
        cpf,
    } = priceObj || {};
    const ctaLink = productUrl || ctaViewDetailsLink;
    const price = salePrice
        ? {
              regularPrice,
              salePrice,
              productType,
              locale,
              currency,
              tierMsg,
              priceDifference: Math.max(regularPrice - salePrice, 0),
              ctoLink,
              productType: productType && productType.toLowerCase(),
              showInCartText: cpf,
              size: 'medium',
          }
        : undefined;
    const ratings =
        typeof product.rating === 'number'
            ? {
                  score: product.rating,
                  reviewsCount: product.numReviews,
              }
            : product.rating;

    const rewardBadge =
        typeof rewardsBadge === 'object'
            ? {
                  message: rewardsBadge.label,
                  tooltipContent: {
                      title: rewardsBadge.tooltipTitle,
                      description: rewardsBadge.tooltipBody,
                  },
              }
            : null;
    //peak the first image, if the format is not correct map it
    const [firstImage] = product.images || [];
    const images =
        !options.resizeImage && (!firstImage || firstImage.src)
            ? product.images
            : (product.images || []).map(image => {
                  return {
                      alt: image.alt || image.altTex,
                      src: getImageLink(image.src || image.image || image.lrg, options.resizeImage),
                  };
              });

    const translations = {
        buttons: {
            purchaseBtnText: getCTAText(product, priceObj),
            productLinkBtnText: ctaViewDetails,
            compareBtnText: 'ADD TO COMPARE',
        },
    };

    const usageLabel = [...(product.usageLabel || [])];
    if (product.isNew) usageLabel.unshift('NEW');
    if (product.energystar) usageLabel.unshift('ENERGY STAR');

    const transformedProduct = {
        ...product,
        // TODO: need for compare cta to work, its using purchaseButton component
        prdClass: product.product_class,
        name: product.title || product.name,
        title: product.title || product.name,
        //TODO: not make local easier with relative pdp links, we can remove this at some point
        productUrl: ctaLink && ctaLink.indexOf('/us-en/shop') > -1 ? Helpers.getRelativePath(ctaLink) : ctaLink,
        // api inconsistency CTO badge comes in as productBadge in VWA and productType in MDP
        productType: product.productBadge || product.productType,
        violatorMessage: product.mktdiff,
        offerMessage: product.violatorMessage || product.violatorMsg,
        specFirstGlance: product.specFirstGlance || product.keyPoints,
        usageLabel,
        images,
        price: priceObj,
    };

    const isAddToQuote = isccf && isOOS && product_type === 'STO';

    const productTileProps = {
        product: transformedProduct,
        isAgent: isccf,
        grossMargin: gmPoints,
        availableInStock: stock.toString(),
        ratings,
        price,
        purchaseBtnIcon: getButtonAction(product, priceObj),
        translations,
        rewardBadge,
        stockIndicator: {
            stockMessage,
            color: stockStatus,
        },
        disablePurchase: isOOS && !isAddToQuote,
        bundleModule: mapBundleProducts(product.bundleComponents, priceObj),
        compare: toggleCompareProduct && {
            onCompare: handleCompare({ ...transformedProduct, price }, toggleCompareProduct),
            compareChecked: isSelected(sku),
        },
        onPurchase: addToCart && handleAddToCart(product, addToCart, priceObj, null, isAddToQuote),
    };

    return productTileProps;
}

export default getProductProps;
